// SPDX-License-Identifier: AGPL-3.0-or-later

import UnicodeEmojis from '@app/features/expressions/utils/UnicodeEmojis';
import type {RoomEmote} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomAvatarSignals from '@app/features/voice_scene/state/RoomAvatarSignals';
import {
	VOICE_ROOM_STAGE_EFFECT_TOPIC,
	VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX,
} from '@fluxer/constants/src/VoiceRoomStage';
import {
	decodeVoiceRoomStageEffect,
	type VoiceRoomStage,
	type VoiceRoomStageEffect,
} from '@fluxer/schema/src/domains/voice_seat/VoiceRoomStageSchemas';
import {type RemoteParticipant, type Room, RoomEvent} from 'livekit-client';
import {useEffect, useRef, useState} from 'react';

// How long the confetti falls and the spotlight stays (the CSS plays over the same time).
export const STAGE_CONFETTI_MS = 3200;
export const STAGE_SPOT_MS = 4000;

export interface RoomStageEffects {
	// A new key per burst, null while none falls.
	confetti: number | null;
	spot: {userId: string; key: number} | null;
}

const NO_EFFECTS: RoomStageEffects = {confetti: null, spot: null};

function hostEmote(effect: Extract<VoiceRoomStageEffect, {type: 'host_emote'}>): RoomEmote | null {
	if (effect.emote !== 'emoji') return {kind: effect.emote};
	const emoji = effect.emoji ?? '';
	return UnicodeEmojis.nameForSurrogate(emoji, false) !== '' ? {kind: 'emoji', emoji} : null;
}

// The stage's one-off moments: packets the API sends into the room on VOICE_ROOM_STAGE_EFFECT_TOPIC. Only a packet
// the server itself wrote counts (its sender identity is empty); anyone in the room can publish on any topic, and one
// who just joined has no participant here yet either. Nothing plays
// without a stage (also a blocked bot's, or one switched off: useRoomStage gives none). A host emote moves the host
// character through RoomAvatarSignals; confetti and the spotlight are returned for the overlay, and one timer ends them.
export function useRoomStageEffects(room: Room | null, stage: VoiceRoomStage | null): RoomStageEffects {
	const current = useRef(stage);
	current.current = stage;
	const [effects, setEffects] = useState(NO_EFFECTS);
	useEffect(() => {
		if (!room) return;
		let shown = NO_EFFECTS;
		let confettiUntil = 0;
		let spotUntil = 0;
		let timer: ReturnType<typeof setTimeout> | null = null;
		let key = 0;
		const publish = (next: RoomStageEffects) => {
			shown = next;
			setEffects(next);
		};
		const schedule = (now: number) => {
			if (timer !== null) clearTimeout(timer);
			const ends = [confettiUntil, spotUntil].filter((end) => end > now);
			timer = ends.length > 0 ? setTimeout(sweep, Math.min(...ends) - now) : null;
		};
		const sweep = () => {
			timer = null;
			const now = Date.now();
			publish({confetti: confettiUntil > now ? shown.confetti : null, spot: spotUntil > now ? shown.spot : null});
			schedule(now);
		};
		const onData = (
			payload: Uint8Array,
			participant?: RemoteParticipant,
			_kind?: unknown,
			topic?: string,
			_encryption?: unknown,
			senderIdentity?: string,
		) => {
			const stage = current.current;
			if (participant !== undefined || senderIdentity !== '' || topic !== VOICE_ROOM_STAGE_EFFECT_TOPIC || !stage)
				return;
			const effect = decodeVoiceRoomStageEffect(payload);
			if (!effect) return;
			const now = Date.now();
			if (effect.type === 'host_emote') {
				const emote = stage.host ? hostEmote(effect) : null;
				if (emote) RoomAvatarSignals.emit({identity: VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX + stage.bot.id, emote});
				return;
			}
			if (effect.type === 'confetti') {
				confettiUntil = now + STAGE_CONFETTI_MS;
				publish({...shown, confetti: ++key});
			} else {
				spotUntil = now + STAGE_SPOT_MS;
				publish({...shown, spot: {userId: effect.user_id, key: ++key}});
			}
			schedule(now);
		};
		room.on(RoomEvent.DataReceived, onData);
		return () => {
			room.off(RoomEvent.DataReceived, onData);
			if (timer !== null) clearTimeout(timer);
			setEffects(NO_EFFECTS);
		};
	}, [room]);
	return effects;
}
